import { useState } from 'react';
import {
  makeStyles,
  Box,
  Chip,
  Typography,
  Button,
  Tooltip,
  Fade
} from '@material-ui/core';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Info as InfoIcon
} from '@material-ui/icons';
import { getDocumentChanges, getChangesCounts, changeColors } from './changesData';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '12px 20px',
    borderRadius: '8px',
    margin: '0 0 16px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease',
    '&.hidden': {
      opacity: 0,
      transform: 'translateY(-10px)',
      pointerEvents: 'none'
    }
  },
  countersSection: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  counterChip: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    fontSize: '0.85rem',
    fontWeight: 500,
    '& .MuiChip-avatar': {
      width: '18px',
      height: '18px',
      fontSize: '0.7rem'
    }
  },
  navigationSection: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  navButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '0.85rem',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      transform: 'translateY(-1px)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        transform: 'none'
      }
    }
  },
  toggleButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      transform: 'translateY(-1px)'
    },
    '&.active': {
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
      borderColor: 'rgba(255, 255, 255, 0.6)'
    }
  },
  currentPosition: {
    fontSize: '0.85rem',
    fontWeight: 500,
    padding: '6px 12px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.25)'
  },
  infoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  badge: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '6px'
  }
}));

const ChangesToolbar = ({
  documentId,
  currentChangeIndex,
  onNavigateChange,
  onToggleChanges,
  changesVisible = true
}) => {
  const classes = useStyles();
  const [isVisible, setIsVisible] = useState(true);

  // Get changes for current document
  const documentChanges = getDocumentChanges(documentId);
  const changeCounts = getChangesCounts(documentId);

  // Don't show toolbar if no changes
  if (!documentChanges || documentChanges.length === 0) {
    return null;
  }

  const totalChanges = documentChanges.length;
  const hasMultipleChanges = totalChanges > 1;

  const handlePrevious = () => {
    if (currentChangeIndex > 0) {
      onNavigateChange(currentChangeIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentChangeIndex < totalChanges - 1) {
      onNavigateChange(currentChangeIndex + 1);
    }
  };

  const handleToggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handleToggleChanges = () => {
    onToggleChanges(!changesVisible);
  };

  return (
    <Fade in={isVisible}>
      <Box className={`${classes.toolbar} ${!isVisible ? 'hidden' : ''}`}>
        {/* Changes counters */}
        <Box className={classes.countersSection}>
          <Box className={classes.infoSection}>
            <InfoIcon style={{ fontSize: '18px', opacity: 0.8 }} />
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Document changes:
            </Typography>
          </Box>

          {changeCounts.added > 0 && (
            <Chip
              size="small"
              label={`${changeCounts.added} added`}
              className={classes.counterChip}
              avatar={
                <span
                  className={classes.badge}
                  style={{ backgroundColor: changeColors.added.color }}
                />
              }
            />
          )}

          {changeCounts.modified > 0 && (
            <Chip
              size="small"
              label={`${changeCounts.modified} modified`}
              className={classes.counterChip}
              avatar={
                <span
                  className={classes.badge}
                  style={{ backgroundColor: changeColors.modified.color }}
                />
              }
            />
          )}

          {changeCounts.deleted > 0 && (
            <Chip
              size="small"
              label={`${changeCounts.deleted} deleted`}
              className={classes.counterChip}
              avatar={
                <span
                  className={classes.badge}
                  style={{ backgroundColor: changeColors.deleted.color }}
                />
              }
            />
          )}

          {changeCounts.newSection > 0 && (
            <Chip
              size="small"
              label={`${changeCounts.newSection} new sections`}
              className={classes.counterChip}
              avatar={
                <span
                  className={classes.badge}
                  style={{ backgroundColor: changeColors.newSection.color }}
                />
              }
            />
          )}
        </Box>

        {/* Navigation controls */}
        <Box className={classes.navigationSection}>
          {hasMultipleChanges && (
            <>
              <Tooltip title="Previous change">
                <span>
                  <Button
                    className={classes.navButton}
                    onClick={handlePrevious}
                    disabled={currentChangeIndex <= 0}
                    startIcon={<PrevIcon />}
                  >
                    Previous
                  </Button>
                </span>
              </Tooltip>

              <Box className={classes.currentPosition}>
                {currentChangeIndex + 1} of {totalChanges}
              </Box>

              <Tooltip title="Next change">
                <span>
                  <Button
                    className={classes.navButton}
                    onClick={handleNext}
                    disabled={currentChangeIndex >= totalChanges - 1}
                    endIcon={<NextIcon />}
                  >
                    Next
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

          <Tooltip title={changesVisible ? "Hide change highlights" : "Show change highlights"}>
            <Button
              className={`${classes.toggleButton} ${changesVisible ? 'active' : ''}`}
              onClick={handleToggleChanges}
              startIcon={changesVisible ? <HideIcon /> : <ShowIcon />}
            >
              {changesVisible ? 'Hide' : 'Show'}
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </Fade>
  );
};

export default ChangesToolbar;
